// eslint-disable-next-line import/order -- This might need to come first so account.scss can override, we should fix this
import Styles from "common/account/login/index.module.scss";

import "common/account/account.scss";
import { type InjectedFormProps, reduxForm } from "redux-form";
import { FormattedMessage } from "react-intl";

import compose from "util/compose";
import InvalidToken from "common/account/invalid_token";
import Button from "common/core/button";
import Password from "common/account/password/password";
import LoadingIndicator from "common/core/loading_indicator";
import { Background } from "common/account/login/components";
import UnauthenticatedAppFrame from "common/app_frame/unauthenticated";
import { validatePassword } from "validators/account";
import { composeValidators, getFormErrors } from "util/form";
import { validatePresence } from "validators/form";
import type { FormErrorsProps } from "util/typed_form";
import { UserTermsOfService, MasterPrivacyPolicyUrl } from "common/tos";

type FormValues = {
  password: string;
};

type Props = {
  invalidToken?: boolean;
  loading?: boolean;
  setPassword: (data: FormValues) => void;
  withWelcomeText?: boolean;
  goBack?: () => void;
};

type FormProps = InjectedFormProps<FormValues, Props>;
type FormError = { type: string };
type InnerProps = FormProps & Props & FormErrorsProps<FormValues, FormError>;

function DeprecatedSetPassword({
  invalidToken = false,
  loading = false,
  invalid,
  pristine,
  handleSubmit,
  setPassword,
  withWelcomeText = true,
  formErrors,
  goBack,
}: InnerProps) {
  const passwordError = formErrors.password?.type;

  return (
    <UnauthenticatedAppFrame>
      {loading && (
        <div className="AccountContainer">
          <LoadingIndicator colour="white" />
        </div>
      )}

      {invalidToken && <InvalidToken context="activation" />}

      {!loading && !invalidToken && (
        <div className={Styles.loginPage}>
          <div className={Styles.loginContent}>
            <div className={Styles.loginBackground}>
              <Background />
            </div>
            <div className={Styles.login}>
              {withWelcomeText ? (
                <>
                  <h1 className={Styles.h1}>
                    <FormattedMessage
                      id="62309040-4733-4958-8c9e-5a1fed564cdc"
                      defaultMessage="Welcome to Notarize!"
                    />
                  </h1>
                  <h3 className={Styles.h3}>
                    <FormattedMessage
                      id="06f2bf1c-f94b-4ed5-843b-af87ad2dcd5f"
                      description="header"
                      defaultMessage="Set a password to get started."
                    />
                  </h3>
                </>
              ) : (
                <h2 className={Styles.h2}>
                  <FormattedMessage
                    id="b07c4d5a-0afe-4e74-89dd-c3bdc89fccb4"
                    defaultMessage="Set your password"
                  />
                </h2>
              )}
              <form onSubmit={handleSubmit(setPassword)}>
                <Password
                  withRequirements
                  autofocus
                  invalid={invalid && !pristine}
                  inputClass={passwordError ? Styles.inputWithError : Styles.input}
                />

                <Button
                  type="submit"
                  data-automation-id="set-password-button"
                  buttonColor="action"
                  buttonSize="large"
                  variant="primary"
                  fullwidth
                  disabled={invalid || pristine}
                >
                  <FormattedMessage
                    id="3399401c-d961-48a1-aafa-c5b65c4e9076"
                    defaultMessage="Set password"
                  />
                </Button>
                <p className={Styles.tou}>
                  <FormattedMessage
                    id="264fd9a8-a0ce-4f14-b5d4-18691861e9ce"
                    defaultMessage="By clicking 'Set password' and continuing, you are agreeing to our {notaryTermsOfUse}. For information on our privacy and data use practices please see our {privacyPolicy}."
                    values={{
                      notaryTermsOfUse: <UserTermsOfService />,
                      privacyPolicy: <MasterPrivacyPolicyUrl />,
                    }}
                  />
                </p>
              </form>
              {goBack && (
                <div className={Styles.backButton}>
                  <Button buttonColor="action" variant="tertiary" onClick={goBack}>
                    <FormattedMessage
                      id="cd939d54-a799-4537-995c-0b9d86363612"
                      defaultMessage="Back"
                    />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </UnauthenticatedAppFrame>
  );
}

export default compose(
  reduxForm<FormValues, Props>({
    form: "setPassword",
    validate: composeValidators(
      validatePassword({ field: "password", label: "Password" }),
      validatePresence({ field: "password", label: "Password" }),
    ),
  }),
  getFormErrors<FormValues, Props & FormProps, FormError>("setPassword"),
)(DeprecatedSetPassword);
